import { FC, useCallback, useEffect, useState } from "react";
import {
  ProductVariation,
  SingleProduct,
  SizeFitLabel,
} from "src/types/graphql.d";
import {
  SizeSelectorDropdown,
  MobileSizeSelectorOverlay,
} from "src/molecules/SizeSelector";
import { getSizeStock } from "src/hooks/useSizeSchemaFilter";
import { ConversionItemAttributes } from "src/utils/constructor-beacon";

interface NewSizeSelectorProps {
  product: SingleProduct;
  variations: ProductVariation[];
  selectedVariationId?: string;
  onSelectVariation: (variationId: string) => void;
  onAddToCart: () => Promise<boolean>;
  addToCartLoading: boolean;
  hasSizeGuide: boolean;
  onOpenSizeGuide?: () => void;
  hasError: boolean;
  disabled: boolean;
  locale: string;
  selectedSchema?: string;
  onSchemaChange?: (schema: string) => void;
  isMobile: boolean;
  sizeFitLabel?: SizeFitLabel | null;
  showSizeFitBanner: boolean;
  sizeFitBanner: React.ReactNode;
  onSizeSelectorOpen?: () => void;
  onSizeSelectorClose?: () => void;
  autoOpenSingleSize?: boolean;
  ariaDescribedBy?: string;
  dataTestId: string;
  onBackInStockPopupChange?: (isOpen: boolean) => void;
  cnstrcItem?: ConversionItemAttributes;
}

export const NewSizeSelector: FC<NewSizeSelectorProps> = ({
  product,
  variations,
  selectedVariationId,
  onSelectVariation,
  onAddToCart,
  addToCartLoading,
  hasSizeGuide,
  onOpenSizeGuide,
  hasError,
  disabled,
  locale,
  selectedSchema,
  onSchemaChange,
  isMobile,
  sizeFitLabel,
  showSizeFitBanner,
  sizeFitBanner,
  onSizeSelectorOpen,
  onSizeSelectorClose,
  autoOpenSingleSize,
  ariaDescribedBy,
  dataTestId,
  onBackInStockPopupChange,
  cnstrcItem,
}) => {
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleOpenMobileDrawer = useCallback(() => {
    onSizeSelectorOpen?.();
    setMobileDrawerOpen(true);
  }, [onSizeSelectorOpen]);

  const handleCloseMobileDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
    onSizeSelectorClose?.();
  }, [onSizeSelectorClose]);

  useEffect(() => {
    if (autoOpenSingleSize && isMobile && !mobileDrawerOpen) {
      handleOpenMobileDrawer();
    }
  }, [autoOpenSingleSize, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SizeSelectorDropdown
        data-testid={dataTestId}
        aria-describedby={ariaDescribedBy}
        aria-invalid={hasError}
        variations={variations}
        selectedVariationId={selectedVariationId}
        onSelectVariation={onSelectVariation}
        hasError={hasError}
        disabled={disabled}
        locale={locale}
        getStockText={getSizeStock}
        onMobileClick={
          isMobile && !disabled ? handleOpenMobileDrawer : undefined
        }
        selectedSchema={selectedSchema}
        onSchemaChange={onSchemaChange}
        product={product}
        onBackInStockPopupChange={onBackInStockPopupChange}
      />

      {sizeFitBanner}

      {isMobile && (
        <MobileSizeSelectorOverlay
          isOpen={mobileDrawerOpen}
          onClose={handleCloseMobileDrawer}
          product={product}
          variations={variations}
          selectedVariationId={selectedVariationId}
          onSelectVariation={onSelectVariation}
          onOpenSizeGuide={onOpenSizeGuide}
          hasSizeGuide={hasSizeGuide}
          addToCartLoading={addToCartLoading}
          onAddToCart={onAddToCart}
          cnstrcItem={cnstrcItem}
          getStockText={getSizeStock}
          sizeFitLabel={
            showSizeFitBanner &&
            (sizeFitLabel === "runs_small" || sizeFitLabel === "runs_large")
              ? sizeFitLabel
              : undefined
          }
          locale={locale}
          selectedSchema={selectedSchema}
          onSchemaChange={onSchemaChange}
          onBackInStockPopupChange={onBackInStockPopupChange}
        />
      )}
    </>
  );
};
